/**
 * @fileoverview Compares Power BI page and filter metadata.
 * Comparison is order-independent for both filters and values within each filter.
 */

import type { NormalizedFilter } from 'utils/powerbi-metadata/filter-normalizer';

export interface PageMetadata {
  activePage: string | null;
  filters: NormalizedFilter[];
}

const getFilterKey = (filter: NormalizedFilter): string => {
  const column = filter?.column ?? '';
  const values = Array.isArray(filter?.values)
    ? [...filter.values].sort((a, b) =>
        JSON.stringify(a).localeCompare(JSON.stringify(b)),
      )
    : [];

  return `${column}: ${JSON.stringify(values)}`;
};

const isSameFilterSets = (
  oldFilters: NormalizedFilter[],
  newFilters: NormalizedFilter[],
): boolean => {
  if (oldFilters.length !== newFilters.length) return false;

  const compareByLocale = (a: string, b: string) => a.localeCompare(b);
  const oldKeys = oldFilters.map(getFilterKey).sort(compareByLocale);
  const newKeys = newFilters.map(getFilterKey).sort(compareByLocale);

  return oldKeys.every((key, index) => key === newKeys[index]);
};

export const comparePageAndFilters = (
  oldMetadata: PageMetadata | null | undefined,
  newMetadata: PageMetadata | null | undefined,
): boolean => {
  if (!oldMetadata || !newMetadata) return false;

  return (
    oldMetadata.activePage === newMetadata.activePage &&
    isSameFilterSets(oldMetadata.filters, newMetadata.filters)
  );
};
